import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * The site logo, read from WordPress at runtime.
 *
 * WordPress holds a logo set (WVC-1627): the main logo plus a version for
 * light surfaces, one for dark surfaces, and the icon cut from each. Both
 * colour versions are rendered on top of each other and CSS shows one: the
 * one for the colour painted behind the logo when the component can read it
 * (a brand band, a fixed `bg-white` header), else the one the nearest
 * `.light` / `.dark` ancestor asks for. A site whose theme returns no versions
 * (an old theme, or a one-colour SVG that follows `--color-logo`) renders the
 * single logo exactly as before.
 */

type VersionSlot = 'on_light' | 'on_dark' | 'symbol_on_light' | 'symbol_on_dark';

interface LogoEntry {
  attachment_id: number;
  url: string;
  svg_content?: string | null;
}

interface LogoMeta extends LogoEntry {
  variants?: Partial<Record<VersionSlot, LogoEntry>> | null;
}

interface WvcLogoProps {
  className?: string;
  style?: React.CSSProperties;
  /**
   * Force one colour version. Unset, the version follows the colour painted
   * behind the logo, or the nearest `.light` / `.dark` ancestor where that
   * colour cannot be read (a photo, a gradient).
   */
  variant?: 'on_light' | 'on_dark';
  /** `symbol`: the icon alone (footer, compact places) when the logo has one; else the full logo. */
  kind?: 'full' | 'symbol';
}

declare const wvcClient:
  | {
    getLogo?: (skipCache?: boolean) => Promise<LogoMeta | null>;
  }
  | undefined;

interface Rendered {
  svgContent: string | null;
  imgUrl: string | null;
}

interface LogoState {
  main: Rendered;
  onLight?: Rendered;
  onDark?: Rendered;
  symbolOnLight?: Rendered;
  symbolOnDark?: Rendered;
}

const EMPTY: Rendered = { svgContent: null, imgUrl: null };

/**
 * Normalises SVG markup for inline rendering. `recolor` (the single-logo
 * fallback only) turns a one-fill logo's fills into `currentColor` so it
 * follows the theme; a version of the set already carries its own colours
 * and is never recoloured.
 */
function processSvgMarkup(raw: string, recolor: boolean): string {
  let svg = raw;
  if (recolor) {
    // Only generated logos (a single fill color) are recolored to the theme via
    // currentColor. A user-supplied brand SVG with multiple fill colors keeps its
    // original colors untouched.
    const distinctFills = new Set(
      (raw.match(/\bfill="([^"]*)"/gi) ?? [])
        .map((m) => m.replace(/\bfill="([^"]*)"/i, '$1').trim().toLowerCase())
        .filter((c) => c && c !== 'none' && c !== 'currentcolor'),
    );
    if (distinctFills.size <= 1) {
      svg = svg.replace(/\bfill="[^"]*"/g, 'fill="currentColor"');
    }
  }
  // Strip hardcoded width/height/style from root <svg> so the component
  // controls sizing, then inject height:100%;width:auto so it fills its container.
  svg = svg.replace(/<svg\b([^>]*)>/i, (_, attrs) => {
    const cleaned = attrs.replace(/\s+(?:width|height|style)="[^"]*"/g, '');
    return `<svg${cleaned} style="height:100%;width:auto;display:block">`;
  });
  return svg;
}

async function fetchLogoData(url: string, recolor: boolean): Promise<Rendered> {
  // Only SVG needs fetching (to inline it for theme recoloring). Everything else
  // renders as a plain <img>, which loads cross-origin without a CORS preflight.
  // We must NOT rely on a fixed raster allow-list: WordPress transcodes large
  // uploads to avif/webp (e.g. logo-scaled.avif), and fetch()-ing those from a
  // different origin (the editor preview) is CORS-blocked. So treat anything that
  // is not an .svg URL as an image and render it directly.
  if (!/\.svg(\?|#|$)/i.test(url)) {
    return { svgContent: null, imgUrl: url };
  }
  try {
    const res = await fetch(url);
    if (!res.ok) return { svgContent: null, imgUrl: url };
    const contentType = res.headers.get('Content-Type') ?? '';
    if (contentType.includes('image/svg+xml')) {
      return { svgContent: processSvgMarkup(await res.text(), recolor), imgUrl: null };
    }
    return { svgContent: null, imgUrl: url };
  } catch {
    // SVG fetch failed (e.g. cross-origin CORS in the editor preview) — fall back
    // to rendering the URL as a plain <img> rather than showing no logo at all.
    return { svgContent: null, imgUrl: url };
  }
}

async function resolveEntry(
  entry: LogoEntry | null | undefined,
  recolor: boolean,
): Promise<Rendered | undefined> {
  if (!entry) return undefined;
  // svg_content is pre-loaded by the theme (PHP reads the file server-side),
  // so no cross-origin fetch is needed and CORS cannot block it.
  if (entry.svg_content) {
    return { svgContent: processSvgMarkup(entry.svg_content, recolor), imgUrl: null };
  }
  return entry.url ? await fetchLogoData(entry.url, recolor) : undefined;
}

// The logo is kept outside the component tree because the tree is torn down far
// more often than the logo changes: Header and Footer each render a WvcLogo, and
// App.tsx keys <Routes> by pathname, so every navigation remounts both. Held in
// component state alone, each remount started empty — and an empty WvcLogo
// renders nothing at all — so the logo vanished and the header reflowed twice
// per navigation, once on the way out and once when it came back.
let logoCache: LogoState | null = null;
// In-flight load, so Header and Footer share one round trip instead of two.
let inFlightLogo: Promise<LogoState | null> | null = null;

const loadLogo = (skipCache: boolean): Promise<LogoState | null> => {
  if (logoCache) return Promise.resolve(logoCache);
  if (inFlightLogo) return inFlightLogo;

  const request: Promise<LogoState | null> = (async () => {
    if (typeof wvcClient?.getLogo !== 'function') return null;
    const meta = await wvcClient.getLogo(skipCache);
    if (!meta) return null;
    const variants = meta.variants ?? {};
    const [onLight, onDark, symbolOnLight, symbolOnDark] = await Promise.all([
      resolveEntry(variants.on_light, false),
      resolveEntry(variants.on_dark, false),
      resolveEntry(variants.symbol_on_light, false),
      resolveEntry(variants.symbol_on_dark, false),
    ]);
    // With no colour version at all the main is the whole logo and follows
    // --color-logo, as before the set; with versions it is only the stand-in
    // for a missing one, in its own colours.
    const main = await resolveEntry(meta, !onLight && !onDark);
    if (!main) return null;
    return { main, onLight, onDark, symbolOnLight, symbolOnDark };
  })()
    .then((state) => {
      // A WVC_LOGO_REFRESH while this was in flight dropped it; writing it back
      // would restore the pre-upload logo.
      if (state && inFlightLogo === request) logoCache = state;
      return state;
    })
    .finally(() => {
      if (inFlightLogo === request) inFlightLogo = null;
    });

  inFlightLogo = request;
  return request;
};

// One listener for the module rather than one per component: the event only
// says "the logo changed", so handling it per component refetched it once for
// every mounted WvcLogo.
const logoRefreshSubscribers = new Set<() => void>();

const invalidateLogo = () => {
  logoCache = null;
  inFlightLogo = null;
  logoRefreshSubscribers.forEach((refetch) => refetch());
};

// The switch: two inherited custom properties flipped by the nearest .light /
// .dark ancestor (never Tailwind's `dark:` variant, which only knows the
// html-level class). Both versions stay laid out — visibility, not display —
// so a CSS-only flip never needs a re-measure and the hidden SVG keeps its
// fitted viewBox. Injected once per document, never per component.
const SWITCH_STYLE_ID = 'wvc-logo-switch';
const SWITCH_CSS = [
  ':root,.light{--wvc-logo-on-light:visible;--wvc-logo-on-dark:hidden}',
  '.dark{--wvc-logo-on-light:hidden;--wvc-logo-on-dark:visible}',
  // A definite row: without it `height: 100%` on the stacked versions does not
  // resolve against the grid's auto track, and each image renders at its
  // natural size (a 1574x660 PNG in a 48px header).
  '.wvc-logo-stack{display:grid;grid-template-rows:100%;height:100%}',
  '.wvc-logo-stack>span{grid-area:1/1;height:100%}',
  '.wvc-logo-on-light{visibility:var(--wvc-logo-on-light)}',
  '.wvc-logo-on-dark{visibility:var(--wvc-logo-on-dark)}',
  // The painted ground read below. Last, and more specific than a `dark` /
  // `light` class passed in className, so it wins over every class.
  '[data-wvc-role="logo"][data-wvc-ground="light"]{--wvc-logo-on-light:visible;--wvc-logo-on-dark:hidden}',
  '[data-wvc-role="logo"][data-wvc-ground="dark"]{--wvc-logo-on-light:hidden;--wvc-logo-on-dark:visible}',
].join('');

const ensureSwitchStyle = () => {
  if (typeof document === 'undefined' || document.getElementById(SWITCH_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SWITCH_STYLE_ID;
  style.textContent = SWITCH_CSS;
  document.head.appendChild(style);
};

// The painted ground. The class switch only sees `.light` / `.dark`, and code
// that paints a header without one — a brand band (`bg-secondary`), a fixed
// `bg-white/95` on a dark site — gets the wrong version. So each logo walks
// from its wrapper up to <html> and stops at the first element that decides:
// - a background image (photo, gradient): undecided, it cannot be read;
// - a colour it cannot read: undecided;
// - a colour at least half opaque: dark or light by WCAG relative luminance,
//   split at 0.179, where black and white text contrast equally;
// - a `light` / `dark` class: undecided, the class switch already covers it.
// The answer goes on the wrapper as data-wvc-ground; undecided removes it, so
// the class switch then works exactly as without this.
type Ground = 'light' | 'dark';
type Rgba = [number, number, number, number];

const GROUND_ATTR = 'data-wvc-ground';
const OPAQUE_ENOUGH = 0.5;
const DARK_LUMINANCE = 0.179;

// Colours defined in sRGB compute to rgb() / rgba().
const LEGACY_RGB = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+))?\s*\)$/;

// Any other form — Tailwind 4 computes its oklch palette and `bg-white/95` (a
// color-mix) to oklch(), oklab() or color(srgb …) — is converted to sRGB by a
// 1x1 canvas. Privacy modes block or randomise the canvas read-back, so a
// self-test gates it; without it, such colours stay unread (undecided).
const CANVAS_SENTINEL = '#010203';
let canvasContext: CanvasRenderingContext2D | null | undefined;

const paintPixel = (ctx: CanvasRenderingContext2D, color: string): Rgba | null => {
  ctx.fillStyle = CANVAS_SENTINEL;
  ctx.fillStyle = color;
  // A colour the canvas cannot parse leaves fillStyle unchanged.
  if (ctx.fillStyle === CANVAS_SENTINEL) return null;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return [r, g, b, a / 255];
};

const readingCanvas = (): CanvasRenderingContext2D | null => {
  if (canvasContext !== undefined) return canvasContext;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const probe = ctx && paintPixel(ctx, 'rgb(10, 200, 30)');
  const honest =
    probe !== null &&
    Math.abs(probe[0] - 10) <= 4 &&
    Math.abs(probe[1] - 200) <= 4 &&
    Math.abs(probe[2] - 30) <= 4 &&
    probe[3] === 1;
  canvasContext = honest ? ctx : null;
  return canvasContext;
};

const colorCache = new Map<string, Rgba | null>();

const toRgba = (color: string): Rgba | null => {
  let rgba = colorCache.get(color);
  if (rgba === undefined) {
    const legacy = LEGACY_RGB.exec(color);
    if (legacy) {
      rgba = [Number(legacy[1]), Number(legacy[2]), Number(legacy[3]), legacy[4] ? Number(legacy[4]) : 1];
    } else {
      const ctx = readingCanvas();
      rgba = ctx ? paintPixel(ctx, color) : null;
    }
    // A header animating its colour inline would otherwise grow it per frame.
    if (colorCache.size > 256) colorCache.clear();
    colorCache.set(color, rgba);
  }
  return rgba;
};

const linear = (channel: number) => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const luminance = ([r, g, b]: Rgba) => 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);

// Under `transition-colors` the computed colour is the start of the fade; the
// version should follow where it ends. A transition read here re-checks once
// more when it ends or is cancelled.
const hookedTransitions = new WeakSet<Animation>();

const fadeTarget = (el: Element, recheck: () => void): string | undefined => {
  for (const animation of el.getAnimations()) {
    if (
      !('transitionProperty' in animation) ||
      animation.transitionProperty !== 'background-color' ||
      animation.playState !== 'running'
    ) {
      continue;
    }
    if (!hookedTransitions.has(animation)) {
      hookedTransitions.add(animation);
      animation.finished.then(recheck, recheck);
    }
    const keyframes = (animation.effect as KeyframeEffect | null)?.getKeyframes() ?? [];
    const end = keyframes[keyframes.length - 1]?.backgroundColor;
    if (typeof end === 'string') return end;
  }
  return undefined;
};

const groundBehind = (wrapper: HTMLElement, recheck: () => void): Ground | undefined => {
  for (let el: Element | null = wrapper; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.backgroundImage !== 'none') return undefined;
    const rgba = toRgba(fadeTarget(el, recheck) ?? style.backgroundColor);
    if (!rgba) return undefined;
    if (rgba[3] >= OPAQUE_ENOUGH) return luminance(rgba) <= DARK_LUMINANCE ? 'dark' : 'light';
    if (el.classList.contains('dark') || el.classList.contains('light')) return undefined;
  }
  return undefined;
};

// Re-checks every mounted logo for changes no ancestor attribute shows.
const groundRechecks = new Set<() => void>();
const recheckAllGrounds = () => groundRechecks.forEach((recheck) => recheck());

if (typeof window !== 'undefined') {
  window.addEventListener('WVC_LOGO_REFRESH', invalidateLogo);
  ensureSwitchStyle();
  // Breakpoint classes (`lg:bg-secondary`).
  window.addEventListener('resize', recheckAllGrounds);
  // Styles that arrive after the first check: Tailwind's browser build (plain
  // WordPress pages, the editor preview) compiles classes into a <style> in
  // <head> after React has rendered, and rewrites it as classes appear.
  new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node instanceof HTMLLinkElement) node.addEventListener('load', recheckAllGrounds, { once: true });
      }),
    );
    recheckAllGrounds();
  }).observe(document.head, { childList: true, subtree: true, characterData: true });
}

// `maxWidth: none` undoes Tailwind's preflight `img { max-width: 100% }`, which
// lets a flex row with a long menu squeeze the logo to 0 px wide.
const IMG_STYLE: React.CSSProperties = { display: 'block', height: '100%', width: 'auto', maxWidth: 'none' };

function Version({
  rendered,
  onError,
}: {
  rendered: Rendered;
  onError?: () => void;
}) {
  if (rendered.svgContent) {
    return (
      <span
        style={{ display: 'block', height: '100%' }}
        dangerouslySetInnerHTML={{ __html: rendered.svgContent }}
      />
    );
  }
  if (rendered.imgUrl) {
    return (
      <img
        src={rendered.imgUrl}
        alt="Logo"
        onError={onError}
        style={IMG_STYLE}
      />
    );
  }
  return null;
}

export function WvcLogo({ className = 'h-10 w-auto', style, variant, kind = 'full' }: WvcLogoProps) {
  // Seeded from the cache so a remount renders the logo it already has, instead
  // of rendering nothing and reflowing the header when it arrives.
  const [logo, setLogo] = useState<LogoState>(() => logoCache ?? { main: EMPTY });
  // A version whose <img> failed to load; the main takes its slot.
  const [broken, setBroken] = useState<{ light?: boolean; dark?: boolean }>({});
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    ensureSwitchStyle();
    // getLogo reaches the editor over postMessage and the theme through a
    // shared client, so the load cannot be aborted; this drops a result whose
    // component has already unmounted.
    let unmounted = false;
    const apply = (state: LogoState | null) => {
      if (!unmounted && state) {
        setBroken({});
        setLogo(state);
      }
    };

    // getLogo() returns cached data instantly on first load, falls back to REST
    // if not pre-loaded.
    loadLogo(false).then(apply);

    // invalidateLogo has already emptied the cache by the time this runs, and
    // skipCache makes the re-read reach WordPress rather than the client cache.
    const onLogoInvalidated = () => {
      loadLogo(true).then(apply);
    };
    logoRefreshSubscribers.add(onLogoInvalidated);

    return () => {
      unmounted = true;
      logoRefreshSubscribers.delete(onLogoInvalidated);
    };
  }, []);

  // Uploaded SVGs often ship with an oversized/padded viewBox (e.g. a full
  // artboard around a small centered logo). Since we don't control the source
  // file, tighten the viewBox to the rendered content's real bounds once it's
  // in the DOM, so the logo fills its box instead of floating small inside
  // empty canvas space. Every inline SVG in the wrapper is fitted — both
  // versions, whichever is visible — and it re-runs on every logo change
  // (including after WVC_LOGO_REFRESH) so a newly uploaded logo is also refit.
  useEffect(() => {
    const svgEls = wrapperRef.current?.querySelectorAll('svg');
    if (!svgEls?.length) return;

    const fitViewBox = () => {
      svgEls.forEach((svgEl) => {
        try {
          const bbox = svgEl.getBBox();
          if (bbox.width > 0 && bbox.height > 0) {
            const pad = Math.max(bbox.width, bbox.height) * 0.02;
            svgEl.setAttribute(
              'viewBox',
              `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`,
            );
          }
        } catch {
          // getBBox() throws if the SVG isn't laid out (display:none ancestor, etc.)
        }
      });
    };

    const raf = requestAnimationFrame(fitViewBox);
    return () => cancelAnimationFrame(raf);
  }, [logo]);

  const { main } = logo;
  const symbols = kind === 'symbol' && logo.symbolOnLight && logo.symbolOnDark;
  const hasVersions = Boolean(logo.onLight || logo.onDark);
  const stacked = !variant && Boolean(symbols || hasVersions);

  // Reads the painted ground before the first paint, then again whenever an
  // ancestor's attributes change (headers switch on scroll by class, style or
  // data-* attributes). The result is written to the DOM, not to state, so a
  // re-check never re-renders.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!stacked || !wrapper) return;
    let active = true;
    let frame = 0;
    let observed: Element[] = [];

    const recheck = () => {
      if (active && !frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          check();
        });
      }
    };
    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.attributeName !== GROUND_ATTR)) recheck();
    });
    const check = () => {
      const ancestors: Element[] = [];
      for (let el = wrapper.parentElement; el; el = el.parentElement) ancestors.push(el);
      if (ancestors.length !== observed.length || ancestors.some((el, i) => el !== observed[i])) {
        observed = ancestors;
        observer.disconnect();
        // Not the wrapper's own data-wvc-ground: writing it would re-check forever.
        observer.observe(wrapper, { attributes: true, attributeFilter: ['class', 'style'] });
        ancestors.forEach((el) => observer.observe(el, { attributes: true }));
      }
      const ground = groundBehind(wrapper, recheck);
      if (!ground) wrapper.removeAttribute(GROUND_ATTR);
      else if (wrapper.getAttribute(GROUND_ATTR) !== ground) wrapper.setAttribute(GROUND_ATTR, ground);
    };

    check();
    groundRechecks.add(recheck);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      groundRechecks.delete(recheck);
      wrapper.removeAttribute(GROUND_ATTR);
    };
  }, [stacked]);

  if (!symbols && !hasVersions) {
    // No set: the single logo, following --color-logo as before.
    if (main.svgContent) {
      return (
        <span
          ref={wrapperRef}
          className={className}
          style={{ display: 'inline-block', ...style }}
          data-wvc-role="logo"
        >
          <span
            style={{ color: 'var(--color-logo, currentColor)', display: 'block', height: '100%' }}
            dangerouslySetInnerHTML={{ __html: main.svgContent }}
          />
        </span>
      );
    }
    if (main.imgUrl) {
      return (
        <span className={className} style={{ display: 'inline-block', ...style }} data-wvc-role="logo">
          <img src={main.imgUrl} alt="Logo" style={IMG_STYLE} />
        </span>
      );
    }
    return null;
  }

  // One version only (e.g. a dark logo set by hand in the Customizer): the
  // main stands in for the missing one.
  const light = (symbols ? logo.symbolOnLight : logo.onLight) ?? main;
  const dark = (symbols ? logo.symbolOnDark : logo.onDark) ?? main;
  const lightRendered = broken.light ? main : light;
  const darkRendered = broken.dark ? main : dark;

  return (
    <span
      ref={wrapperRef}
      className={className}
      style={{ display: 'inline-block', ...style }}
      data-wvc-role="logo"
    >
      {variant === 'on_light' ? (
        <span style={{ display: 'block', height: '100%' }}>
          <Version rendered={lightRendered} onError={() => setBroken((b) => ({ ...b, light: true }))} />
        </span>
      ) : variant === 'on_dark' ? (
        <span style={{ display: 'block', height: '100%' }}>
          <Version rendered={darkRendered} onError={() => setBroken((b) => ({ ...b, dark: true }))} />
        </span>
      ) : (
        <span className="wvc-logo-stack">
          <span className="wvc-logo-on-light">
            <Version rendered={lightRendered} onError={() => setBroken((b) => ({ ...b, light: true }))} />
          </span>
          <span className="wvc-logo-on-dark" aria-hidden="true">
            <Version rendered={darkRendered} onError={() => setBroken((b) => ({ ...b, dark: true }))} />
          </span>
        </span>
      )}
    </span>
  );
}
